// The Shhh Baby Button with the Sound and Timer chips under it, and the Sound sheet they open.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track } from '@/lib/analytics';
import { SoundGlyph } from '@/features/sounds/glyphs';
import { SOUNDS, soundById, TIMER_OPTIONS, type SoundId, type TimerMin } from './catalog';
import { useSound } from './SoundProvider';
import './sound.css';
import { art, useSkin } from '@/lib/skin';

export function BabyButton({ children }: { children?: React.ReactNode }) {
  const snd = useSound();
  const s = soundById(snd.soundId);
  const [sheet, setSheet] = useState<null | 'sound' | 'timer'>(null);
  const soundChip = useRef<HTMLButtonElement>(null);
  const timerChip = useRef<HTMLButtonElement>(null);
  const label = `${snd.playing ? 'Stop' : 'Play'} ${s.name}${snd.timerMin ? `, ${snd.timerMin} minute timer` : ''}`;

  return (
    <div className="shush">
      <button
        className={`baby-button${snd.playing ? ' is-on' : ''}`}
        type="button"
        aria-pressed={snd.playing}
        aria-label={label}
        style={{ ['--breath' as string]: `${s.breath}s` }}
        onClick={() => { track('baby_button_tap', { sound: snd.soundId, action: snd.playing ? 'stop' : 'play' }, 'baby_button'); snd.toggle('baby_button'); }}
      >
        <span className="ring" aria-hidden="true" />
        <span className="disc" aria-hidden="true">Shhh</span>
      </button>
      <div className="soothe" role="group" aria-label="Sound and timer">
        <button ref={soundChip} className="chip" type="button" aria-haspopup="dialog" onClick={() => setSheet('sound')}>
          <img src={art(s.icon, `sound-${s.id}`)} alt="" />
          <span>{s.name}</span>
          <Chevron />
        </button>
        <button ref={timerChip} className={`chip${snd.playing && snd.timerMin ? ' is-live' : ''}`} type="button" aria-haspopup="dialog" onClick={() => setSheet('timer')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 2h4" /><path d="m12 14 3-3" /><circle cx="12" cy="14" r="8" /></svg>
          <span>{snd.timerLabel()}</span>
          <Chevron />
        </button>
      </div>
      {children}
      <SoundSheet open={sheet} onClose={() => { const back = sheet === 'timer' ? timerChip : soundChip; setSheet(null); back.current?.focus(); }} />
    </div>
  );
}

function Chevron() {
  return <svg className="chev" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>;
}

/** Bottom sheet: 12 sounds, the timer, the safety line. In the glass skin: an inset glass sheet with solid sound tiles
 *  (a blue line glyph in a soft tile) and a five-option timer row.
 *  Opened while quiet, a tap previews the sound and closing the sheet stops it (the Shhh button is for real playing).
 *  Opened while a sound plays, a tap switches to the new sound and it keeps playing after the sheet closes. */
export function SoundSheet({ open, onClose }: { open: null | 'sound' | 'timer'; onClose: () => void }) {
  const snd = useSound();
  const ref = useRef<HTMLDialogElement>(null);
  const s = soundById(snd.soundId);
  const playingAtOpen = useRef(false);
  const glass = useSkin() === 'glass';
  const { t } = useTranslation('sounds');
  // the sound pill shows the sounds, the timer pill only the timer (kept while the sheet slides away)
  const [mode, setMode] = useState<'sound' | 'timer'>('sound');
  useEffect(() => { if (open) setMode(open); }, [open]);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      playingAtOpen.current = snd.playing;
      d.showModal();
      const target = d.querySelector<HTMLInputElement>(open === 'timer' ? 'input[name="bp-timer"]:checked' : 'input[name="bp-sound"]:checked');
      target?.focus({ preventScroll: true });
      if (open === 'timer') target?.closest('fieldset')?.scrollIntoView({ block: 'nearest' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    const d = ref.current;
    if (!d?.open) return;
    if (!playingAtOpen.current && snd.playing) snd.stop('user'); // end the preview
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { d.close(); onClose(); return; }
    d.classList.add('closing');
    d.addEventListener('animationend', () => { d.classList.remove('closing'); d.close(); onClose(); }, { once: true });
  };

  return (
    <dialog
      ref={ref}
      className="sheet sound-sheet"
      aria-labelledby="sound-sheet-title"
      onCancel={(e) => { e.preventDefault(); close(); }}
      onClick={(e) => {
        const d = ref.current!;
        const r = d.getBoundingClientRect();
        if (e.target === d && (e.clientY < r.top || e.clientY > r.bottom || e.clientX < r.left || e.clientX > r.right)) close();
      }}
    >
      <div className="sheet-grab" aria-hidden="true" />
      <div className="sheet-head">
        <h2 id="sound-sheet-title">{mode === 'timer' ? t('sheet.timerTitle') : t('sheet.title')}</h2>
        <button className="sheet-done" type="button" onClick={close}>{t('sheet.done')}</button>
      </div>
      {mode === 'sound' && <>
      <p className="sheet-note" aria-live="polite"><span className="vh">{s.name}: </span>{t(`library.notes.${s.id}`, { defaultValue: s.note })}</p>
      {!playingAtOpen.current && <p className="hint">{glass ? t('sheet.hint') : t('sheet.hintFelt')}</p>}
      <fieldset className="sound-grid" aria-describedby="sound-note">
        <legend className="vh">{t('sheet.sound')}</legend>
        {SOUNDS.map((x) => (
          <label className="sound" key={x.id}>
            <input
              type="radio"
              name="bp-sound"
              value={x.id}
              checked={snd.soundId === x.id}
              onChange={() => snd.choose(x.id as SoundId)}
              onClick={() => { if (x.id === snd.soundId && !snd.playing) snd.choose(x.id); }}
            />
            {glass ? <SoundGlyph id={x.id} className="sound-glyph" /> : <img src={art(x.icon, `sound-${x.id}`)} alt="" loading="lazy" />}
            <span>{x.name}</span>
          </label>
        ))}
      </fieldset>
      </>}
      {mode === 'timer' && <fieldset>
        <legend className="sheet-note">{t('sheet.timerLead', { sound: s.name })}</legend>
        <div className="timer-row">
          {TIMER_OPTIONS.map((m) => (
            <label className="pill" key={m}>
              <input type="radio" name="bp-timer" value={m} checked={snd.timerMin === m} onChange={() => snd.setTimer(m as TimerMin)} aria-label={m ? t('timer.minA11y', { count: m }) : t('timer.off')} />
              {m ? t('timer.min', { count: m }) : glass ? <span aria-hidden="true">{t('timer.offShort')}</span> : <><span className="t-long" aria-hidden="true">{t('timer.off')}</span><span className="t-short" aria-hidden="true">{t('timer.offShort')}</span></>}
            </label>
          ))}
        </div>
      </fieldset>}
      <p className="sheet-safe">{t('sheet.safety')}</p>
    </dialog>
  );
}
